/**
 * Mobile Lot Card DOM Component
 *
 * Renders a lot card for mobile ISM view showing lot details (number, size, type, facing).
 * Card is positioned at bottom as overlay. Back button is positioned separately (top-left).
 *
 * @module nativeInteractiveSiteMap/components/mobileLotCard.dom
 */

import { backButtonIconSvg } from 'constants/svg-assets';
import getLotGroupCardHtml from './lotGroupCard.dom';

/**
 * Creates the mobile lot card (bottom-sheet overlay, no back button)
 * @param {Object} lot - Lot object with lotNumber, group, size, type, facing
 * @param {Function} onCardClick - Callback when card is clicked
 * @returns {Object} Component object with getElement() and remove() methods
 */
export default function createMobileLotCardDom(lot = {}, onCardClick = () => {}) {
  let cardElement = null;
  let cardContentElement = null;

  function buildLotCardHtml(lotData = {}) {
    const rawLotNumber = typeof lotData.displayLabel === 'string'
      ? lotData.displayLabel.trim()
      : lotData.displayLabel;
    const rawGroup = typeof lotData.lotGroupName === 'string'
      ? lotData.lotGroupName.trim()
      : lotData.lotGroupName;
    const hasLotNumber = Boolean(rawLotNumber);
    const hasGroup = Boolean(rawGroup);

    return getLotGroupCardHtml({
      lotNumber: hasLotNumber ? `Lot ${rawLotNumber}${hasGroup ? ' -' : ''}` : null,
      group: hasGroup ? `Group ${rawGroup}` : null,
      size: lotData.formattedLotSize || null,
      type: lotData.lotType || null,
      facing: lotData.lotFacing || null,
    });
  }

  function updateContent(lotData = {}) {
    if (!cardContentElement) {
      return;
    }

    cardContentElement.innerHTML = buildLotCardHtml(lotData);
  }

  function getElement() {
    if (!cardElement) {
      // Build the lot card with reused nhs-c-lot-details-card structure
      const lotCardHtml = buildLotCardHtml(lot);

      const html = `
        <div class="nhs-c-native-interactive-site-map__mobile-lot-card">
          <div data-mobile-lot-card-content class="nhs-u-padding--300">
            ${lotCardHtml}
          </div>
        </div>
      `;

      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      cardElement = wrapper.firstElementChild;
      cardContentElement = cardElement.querySelector('[data-mobile-lot-card-content]');
      cardElement.addEventListener('click', onCardClick);
    }

    return cardElement;
  }

  function remove() {
    if (cardElement) {
      cardElement.removeEventListener('click', onCardClick);
      cardElement.remove();
      cardElement = null;
      cardContentElement = null;
    }
  }

  return { getElement, remove, updateContent };
}

/**
 * Creates a back button positioned at top-left (fixed)
 * @param {Function} onBackClick - Callback when back button is clicked
 * @returns {Object} Component object with getElement() and remove() methods
 */
export function createMobileLotCardBackButton(onBackClick = () => {}) {
  let buttonElement = null;

  function getElement() {
    if (!buttonElement) {
      const html = `
        <button
          class="nhs-c-native-interactive-site-map__mobile-lot-card-back-button"
          type="button"
          aria-label="Back to detail view"
        >
          ${backButtonIconSvg}
        </button>
      `;

      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      buttonElement = wrapper.firstElementChild;
      buttonElement.addEventListener('click', onBackClick);
    }

    return buttonElement;
  }

  function remove() {
    if (buttonElement) {
      buttonElement.remove();
      buttonElement = null;
    }
  }

  return { getElement, remove };
}
